import React, { useState, useEffect } from 'react';
import { displayErrorAlert, displaySuccessAlert } from 'services/alerts';
import TeamsService from 'services/teams';
import GroupInviteLinksService from 'services/group_invite_links';
import OrganizationsService from 'services/organizations';
import { generateInviteUrl } from 'utils/inviteLinks';
import styles from './styles.module.scss';

/**
 * Standalone component that handles the invite button with copy functionality
 * Makes a direct API call to check if the group has an invite link
 */
const InviteButtonWithCopy = ({ 
  groupId, 
  teamId,
  organizationId,
  organization,
  groupName,
  onClick, 
  copyGroupInviteLink,
  fetchInviteLinks 
}) => {
  const [hasInviteLink, setHasInviteLink] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [justCopied, setJustCopied] = useState(false);
  
  // Store the invite links data for copy functionality
  const [inviteLinksData, setInviteLinksData] = useState([]);
  
  // State to store the effective group ID
  const [effectiveGroupId, setEffectiveGroupId] = useState(groupId);
  
  // Add a refresh counter to force updates when fetchInviteLinks is called
  const [refreshCounter, setRefreshCounter] = useState(0);
  
  // Determine which group ID to use based on view level
  useEffect(() => {
    // Reset state when dependencies change
    setIsLoading(true);
    
    console.log('[InviteButton] Determining group ID with params:', { 
      groupId, 
      teamId, 
      organizationId 
    });
    
    if (groupId) {
      // If we have a direct group ID, use it (simplest case)
      console.log(`[InviteButton] Using provided group ID: ${groupId}`);
      setEffectiveGroupId(groupId);
      return;
    }
    
    if (teamId) {
      // If viewing a team but no specific group, use the direct team endpoint with supportsDefaults
      console.log(`[InviteButton] Getting team with ID: ${teamId}`);
      
      // Use the getTeam method with supportsDefaults=true to include default groups
      TeamsService.getTeam(teamId, true)
        .then(team => {
          console.log(`[InviteButton] Got team data:`, team);
          
          // Check if team has any groups
          if (!team || !team.groups || team.groups.length === 0) {
            console.error(`[InviteButton] Team ${teamId} has no groups`);
            return;
          }
          
          // Try to find a default group first
          const defaultGroup = team.groups.find(g => g.is_default === true);
          
          if (defaultGroup) {
            console.log(`[InviteButton] Using team's default group: ${defaultGroup.id}`);
            setEffectiveGroupId(defaultGroup.id);
          } else {
            // If no default group found, use the first group
            console.log(`[InviteButton] No default group found, using first group: ${team.groups[0].id}`);
            setEffectiveGroupId(team.groups[0].id);
          }
        })
        .catch(error => {
          console.error('[InviteButton] Failed to get team:', error);
          
          // Fallback to the alternative method if single team endpoint fails
          console.log('[InviteButton] Falling back to getting all teams');
          
          if (!organizationId) {
            console.error('[InviteButton] Cannot fallback - missing organizationId');
            return;
          }
          
          TeamsService.getTeams(organizationId, true)
            .then(response => {
              const teams = response.data || [];
              console.log(`[InviteButton] Fallback got ${teams.length} teams`);
              
              const team = teams.find(t => t.id === teamId);
              if (!team || !team.groups || team.groups.length === 0) {
                console.error(`[InviteButton] Fallback could not find team with groups`);
                return;
              }
              
              const defaultGroup = team.groups.find(g => g.is_default === true);
              if (defaultGroup) {
                console.log(`[InviteButton] Using fallback team's default group: ${defaultGroup.id}`);
                setEffectiveGroupId(defaultGroup.id);
              } else {
                console.log(`[InviteButton] Using fallback team's first group: ${team.groups[0].id}`);
                setEffectiveGroupId(team.groups[0].id);
              }
            })
            .catch(fallbackError => {
              console.error('[InviteButton] Fallback also failed:', fallbackError);
            });
        });
      return;
    }
    
    if (organizationId) {
      // If viewing all athletes (org level), use the organization data from Redux
      console.log(`[InviteButton] Using organization data from Redux for ID: ${organizationId}`);
      
      // Check if we have organization data from Redux
      if (organization && organization.default_group_id) {
        console.log(`[InviteButton] Using organization's default_group_id: ${organization.default_group_id}`);
        setEffectiveGroupId(organization.default_group_id);
        return;
      }
      
      // If we don't have default_group_id in the organization data, fall back to getting teams
      console.log('[InviteButton] No default_group_id in organization data, falling back to teams');
      TeamsService.getTeams(organizationId, true)
        .then(teamsResponse => {
          // Only process this if it's a valid response from the fallback
          if (!teamsResponse || !teamsResponse.data) return;
          
          const teams = teamsResponse.data;
          console.log(`[InviteButton] Fallback got ${teams.length} teams`);
          
          // Look for a default team
          const defaultTeam = teams.find(t => t.is_default === true);
          
          if (defaultTeam && defaultTeam.groups && defaultTeam.groups.length > 0) {
            // Find default group in default team
            const defaultGroup = defaultTeam.groups.find(g => g.is_default === true);
            
            if (defaultGroup) {
              console.log(`[InviteButton] Using fallback default team's default group: ${defaultGroup.id}`);
              setEffectiveGroupId(defaultGroup.id);
            } else {
              console.log(`[InviteButton] Using fallback default team's first group: ${defaultTeam.groups[0].id}`);
              setEffectiveGroupId(defaultTeam.groups[0].id);
            }
          } else if (teams.length > 0 && teams[0].groups && teams[0].groups.length > 0) {
            console.log(`[InviteButton] Using fallback first team's first group: ${teams[0].groups[0].id}`);
            setEffectiveGroupId(teams[0].groups[0].id);
          }
        })
        .catch(error => {
          console.error('[InviteButton] Error getting teams:', error);
        });
      return;
    }
    
    // If no ID criteria provided at all
    setIsLoading(false);
  }, [groupId, teamId, organizationId, organization]);

  // Add a manual refresh method
  const refreshInviteLinks = () => {
    console.log('[InviteButton] Manual refresh triggered');
    setRefreshCounter(prev => prev + 1);
    setJustCopied(false);
  };
  
  // Listen for creation or deletion of invite links
  useEffect(() => {
    // This will hook into the global window events
    const handleInviteLinkChange = () => {
      console.log('[InviteButton] Detected invite link change event');
      refreshInviteLinks();
    };
    
    // Add event listeners for invite link changes
    window.addEventListener('inviteLinkCreated', handleInviteLinkChange);
    window.addEventListener('inviteLinkDeleted', handleInviteLinkChange);
    
    return () => {
      // Clean up event listeners
      window.removeEventListener('inviteLinkCreated', handleInviteLinkChange);
      window.removeEventListener('inviteLinkDeleted', handleInviteLinkChange);
    };
  }, []);
  
  // Check for invite links when we have determined the effective group ID or when refresh is triggered
  useEffect(() => {
    console.log(`[InviteButton] Checking invite links - groupId: ${effectiveGroupId}, refresh: ${refreshCounter}`);
    
    if (!effectiveGroupId) {
      console.log('[InviteButton] No effective group ID available, not fetching invite links');
      setIsLoading(false);
      return;
    }
    
    console.log(`[InviteButton] Checking for invite links for effective group ${effectiveGroupId}`);
    setIsLoading(true);
    
    // Use the same service that the Redux actions use
    GroupInviteLinksService.getGroupInviteLinks(effectiveGroupId)
      .then(response => {
        console.log(`[InviteButton] Got API response for group ${groupId}:`, response);
        
        // Process the API response to determine if this group has an active invite link
        let foundActiveLink = false;
        const data = response.data || response;
        
        if (Array.isArray(data)) {
          // Array format
          foundActiveLink = data.some(link => 
            link.group_id.toString() === effectiveGroupId.toString() && link.is_active === true
          );
        } else if (data && typeof data === 'object') {
          // Object format
          if (data.is_active === true && 
              data.group_id && 
              data.group_id.toString() === effectiveGroupId.toString()) {
            foundActiveLink = true;
          } else if (data.data && Array.isArray(data.data)) {
            // Nested array format
            foundActiveLink = data.data.some(link => 
              link.group_id.toString() === effectiveGroupId.toString() && link.is_active === true
            );
          }
        }
        
        console.log(`[InviteButton] Effective group ${effectiveGroupId} has active link: ${foundActiveLink}`);
        setHasInviteLink(foundActiveLink);
        setIsLoading(false);
        
        // Store the complete invite links data for the copy functionality
        setInviteLinksData(Array.isArray(data) ? data : 
          (Array.isArray(data.data) ? data.data : 
            (data.is_active ? [data] : [])));
      })
      .catch(error => {
        console.error('[InviteButton] Error checking invite links:', error);
        console.log('[InviteButton] Check the server logs for more information. API endpoint: /group-invite-links/groups/' + effectiveGroupId);
        setIsLoading(false);
      });
  }, [effectiveGroupId, refreshCounter]);
  
  // Track team name - group name is passed directly from props
  const [currentTeamName, setCurrentTeamName] = useState(null);
  
  // Update team name when teamId changes
  useEffect(() => {
    if (teamId) {
      TeamsService.getTeam(teamId)
        .then(team => {
          if (team && team.name) {
            console.log(`[InviteButton] Found team name for ID ${teamId}: ${team.name}`);
            setCurrentTeamName(team.name);
          }
        })
        .catch(error => {
          console.error('[InviteButton] Error getting team name:', error);
        });
    } else {
      setCurrentTeamName(null);
    }
  }, [teamId]);
  
  const handleInviteClick = (e) => {
    // Pass the effective group ID, teamId, and actual names to the click handler
    // Use the group name passed from props instead of making an API call
    console.log(`[InviteButton] Handling invite click with group: ${effectiveGroupId}(${groupName}), team: ${teamId}(${currentTeamName})`);
    onClick(e, effectiveGroupId, teamId, currentTeamName, groupName);
  };
  
  const handleCopyClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    
    try {
      // If we already have the invite links data, use it
      if (inviteLinksData.length > 0) {
        copyInviteLinkFromData(inviteLinksData);
      } else {
        // Otherwise make a fresh API call to get the latest data
        console.log('[InviteButton] Fetching fresh data for copying invite link');
        GroupInviteLinksService.getGroupInviteLinks(effectiveGroupId)
          .then(response => {
            const data = response.data || response;
            const processedData = Array.isArray(data) ? data : 
              (Array.isArray(data.data) ? data.data : 
                (data.is_active ? [data] : []));
                
            copyInviteLinkFromData(processedData);
          })
          .catch(error => {
            console.error('[InviteButton] Error fetching invite link for copy:', error);
            displayErrorAlert('Could not fetch invite link');
          });
      }
    } catch (error) {
      console.error('[InviteButton] Error copying invite link:', error);
      displayErrorAlert('Failed to copy invite link');
    }
  };
  
  // Function to handle the actual copying
  const copyInviteLinkFromData = (inviteLinks) => {
    try {
      // Attempt to get the active link for this group
      const activeLink = inviteLinks.find(link => 
        link.group_id.toString() === effectiveGroupId.toString() && 
        link.is_active === true
      );
      
      if (activeLink && activeLink.invite_code) {
        // Use the shared utility function to generate the invite URL
        const fullUrl = generateInviteUrl(activeLink.invite_code);
        
        console.log('[InviteButton] Copying URL to clipboard:', fullUrl);
        
        navigator.clipboard.writeText(fullUrl)
          .then(() => {
            // Show success message
            displaySuccessAlert('Invite link copied to clipboard');
            console.log('[InviteButton] Successfully copied URL');
            
            // Show checkmark animation
            setJustCopied(true);
            
            // Reset back to copy icon after 1 second
            setTimeout(() => {
              setJustCopied(false);
            }, 1000);
          })
          .catch(err => {
            console.error('[InviteButton] Could not copy text: ', err);
            displayErrorAlert('Failed to copy invite link');
          });
      } else {
        console.error('[InviteButton] No active invite link found in data:', inviteLinks);
        displayErrorAlert('No active invite link found for this group');
      }
    } catch (error) {
      console.error('[InviteButton] Error in copy function:', error);
      displayErrorAlert('Failed to copy invite link');
    }
  };
  
  return (
    <span className={styles.inviteButton} onClick={handleInviteClick}>
      <span className={styles.inviteIcon}>
        <svg width="16" height="17" viewBox="0 0 18 18" fill="#1976d2" style={{ position: 'relative', top: '0px' }}>
          <path d="M11.25 9c1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3 1.34 3 3 3zm-6.75-1.5V5.25H3v2.25H.75v1.5H3v2.25h1.5V9h2.25V7.5H4.5zm6.75 3c-2 0-6 1-6 3v1.5h12v-1.5c0-2-4-3-6-3z"/>
        </svg>
      </span>
      Invite
      {hasInviteLink && (
        <span className={styles.copyLinkIcon} onClick={handleCopyClick}>
        {justCopied ? (
          // Checkmark icon (displays for 1 second after successful copy)
          <svg width="16" height="16" viewBox="0 0 18 18" fill="#4CAF50">
            <path d="M6.75 12.127L3.622 9l-1.065 1.057L6.75 14.25 15.75 5.25l-1.057-1.057-7.943 7.934z"/>
          </svg>
        ) : (
          // Regular copy icon
          <svg width="16" height="16" viewBox="0 0 18 18" fill="#1976d2">
            <path d="M12 0.75H3c-0.825 0-1.5 0.675-1.5 1.5v10.5h1.5V2.25h9V0.75zm2.25 3H6c-0.825 0-1.5 0.675-1.5 1.5v10.5c0 0.825 0.675 1.5 1.5 1.5h8.25c0.825 0 1.5-0.675 1.5-1.5V5.25c0-0.825-0.675-1.5-1.5-1.5zm0 12H6V5.25h8.25v10.5z"/>
          </svg>
        )}
      </span>
      )}
    </span>
  );
};

export default InviteButtonWithCopy;
